import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sitesApi, layoutsApi, ApiError, libraryApi } from '../../api/client';
import { brandPackagesApi } from '../../api/modules/brandPackages';
import { deriveComponentColors, isGlobalPalette } from '../../utils/deriveComponentColors';
import type { Component, Site } from '../../schemas/validation';
import type { LayoutEditorState } from './useLayoutEditorState';
import { getDefaultDataForType } from './componentDefaults';
import { insertComponentAtPosition } from './layoutComponentInsert';
import { LAYOUT_MODE_COMPONENTS } from '../../components/ComponentLibraryModal/componentOptions';
import type { ToastType, ToastOptions } from '../../components/Toast';

const LAYOUT_COMPONENT_LABELS: Record<string, string> = {
  navbar: 'Navigation Bar',
  'navigation-menu': 'Expandable Navigation Bar',
  footer: 'Footer',
  'content-area': 'Content Area',
};

interface UseLayoutEditorHandlersProps {
  siteId: string | undefined;
  layoutId: string | undefined;
  isNewLayout: boolean;
  state: LayoutEditorState;
  showToast: (message: string, type?: ToastType, options?: string | ToastOptions) => void;
}

export const useLayoutEditorHandlers = ({
  siteId,
  layoutId,
  isNewLayout,
  state,
  showToast,
}: UseLayoutEditorHandlersProps) => {
  const navigate = useNavigate();
  const [isAddingComponent, setIsAddingComponent] = useState(false);

  const closeComponentLibrary = useCallback(() => {
    state.setShowComponentLibraryModal(false);
    state.setInsertPosition(null);
  }, [state]);

  const appendComponentToLayout = useCallback(
    (newComponent: Component) => {
      const insertPosition = state.insertPosition;
      state.setComponents(prev => insertComponentAtPosition(prev, newComponent, insertPosition));
      state.setHasUnsavedChanges(true);
      closeComponentLibrary();
      showToast('Component added. Save the layout to keep your changes.', 'success');
    },
    [state, closeComponentLibrary, showToast]
  );

  // Load theme colors only (internal helper)
  const fetchThemeColors = useCallback(async (themeName: string): Promise<any> => {
    const themeResponse = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/themes/${themeName}?_t=${Date.now()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      }
    );

    if (!themeResponse.ok) {
      throw new Error(`Failed to load theme: ${themeResponse.status} ${themeResponse.statusText}`);
    }

    return themeResponse.json();
  }, []);

  // Load site colors - checks brand package first, then falls back to theme
  const loadSiteColors = useCallback(
    async (siteData: Site, showSuccessNotification: boolean = false) => {
      try {
        state.setIsLoadingTheme(true);
        state.setThemeError(null);

        const brandPackageId = (siteData as any).brandPackageId as string | undefined;

        // Priority 1: Brand package
        if (brandPackageId) {
          try {
            console.log('🎨 LayoutEditor: Loading brand package:', brandPackageId);
            const brandPackage = await brandPackagesApi.get(brandPackageId);
            if (brandPackage) {
              console.log('🎨 LayoutEditor: Using brand package colors:', brandPackage.name);

              // Re-derive colors from palette to ensure we have latest color derivations
              const palette = (brandPackage.colors as any)?._palette;
              let derivedColors = brandPackage.colors;

              if (palette && isGlobalPalette(palette)) {
                console.log(
                  '🎨 LayoutEditor: Re-deriving colors from palette for latest properties'
                );
                derivedColors = {
                  _palette: palette,
                  ...deriveComponentColors(palette),
                } as any;
              }

              const colors = {
                ...derivedColors,
                fontFamily: brandPackage.fonts?.bodyFont || 'Inter, system-ui, sans-serif',
                headingFont: brandPackage.fonts?.headingFont || 'Inter, system-ui, sans-serif',
                fontSize: brandPackage.fonts?.baseSize || '16px',
              };
              state.setThemeColors(colors);
              state.setCurrentThemeName(`Brand: ${brandPackage.name}`);

              if (showSuccessNotification) {
                showToast(`Brand package "${brandPackage.name}" loaded successfully!`, 'success');
              }
              return;
            }
          } catch (bpError) {
            console.warn('Failed to load brand package, falling back to theme:', bpError);
          }
        }

        // Priority 2: Theme colors
        if (siteData.theme) {
          console.log('🎨 LayoutEditor: Loading theme colors for:', siteData.theme);
          const themeData = await fetchThemeColors(siteData.theme);
          console.log('🎨 LayoutEditor: Loaded theme data:', themeData);

          state.setThemeColors(themeData.customColors);
          state.setCurrentThemeName(siteData.theme);

          if (showSuccessNotification) {
            showToast(
              `Theme "${themeData.name || siteData.theme}" loaded successfully!`,
              'success'
            );
          }
        }
      } catch (error) {
        console.error('Failed to load site colors:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        state.setThemeError(errorMessage);
        showToast(`Failed to load colors: ${errorMessage}`, 'error');
      } finally {
        state.setIsLoadingTheme(false);
      }
    },
    [state, showToast, fetchThemeColors]
  );

  // Keep for backwards compatibility
  const loadThemeColors = useCallback(
    async (themeName: string, showSuccessToast: boolean = false) => {
      if (!themeName) return;

      // If we have a site, use loadSiteColors to check brand package first
      if (state.site) {
        await loadSiteColors(state.site, showSuccessToast);
        return;
      }

      // Fallback: load theme only (no site context)
      try {
        state.setIsLoadingTheme(true);
        state.setThemeError(null);

        const themeData = await fetchThemeColors(themeName);
        state.setThemeColors(themeData.customColors);
        state.setCurrentThemeName(themeName);

        if (showSuccessToast) {
          showToast(`Theme "${themeData.name || themeName}" loaded successfully!`, 'success');
        }
      } catch (error) {
        console.error('Failed to load theme colors:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        state.setThemeError(errorMessage);
        showToast(`Failed to load theme: ${errorMessage}`, 'error');
      } finally {
        state.setIsLoadingTheme(false);
      }
    },
    [state, showToast, fetchThemeColors, loadSiteColors]
  );

  const loadLayoutData = useCallback(async () => {
    if (!siteId) {
      navigate('/dashboard');
      return;
    }

    try {
      state.setIsLoading(true);

      const allSites = await sitesApi.list();
      const currentSite = allSites.find(s => s.id === siteId);

      if (!currentSite) {
        console.error('Site not found in user sites');
        navigate('/dashboard');
        return;
      }

      state.setSite(currentSite);

      // Load colors (brand package takes priority over theme)
      await loadSiteColors(currentSite);

      if (!isNewLayout) {
        try {
          const layoutData = await layoutsApi.get(layoutId!);
          state.setLayout(layoutData);
          state.setLayoutName(layoutData.name);
          state.setLayoutDescription(layoutData.description || '');
          state.setIsDefault(layoutData.isDefault || false);
          state.setComponents(layoutData.components || []);
        } catch (layoutError) {
          console.error('Failed to load layout:', layoutError);
          navigate(`/sites/${siteId}`);
        }
      } else {
        const contentAreaComponent: Component = {
          id: `content-area-${Date.now()}`,
          type: 'content-area',
          data: {
            label: 'Page Content',
            minHeight: 200,
            backgroundColor: 'transparent',
            maxWidth: 'container',
            centered: true,
            padding: { top: 0, bottom: 0, left: 0, right: 0 },
          },
          order: 0,
          visible: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        state.setComponents([contentAreaComponent]);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate('/login');
      } else {
        console.error('Failed to load layout data:', error);
        navigate('/dashboard');
      }
    } finally {
      state.setIsLoading(false);
    }
  }, [siteId, layoutId, isNewLayout, navigate, state, loadThemeColors]);

  const handleComponentsChange = useCallback(
    (newComponents: Component[]) => {
      state.setComponents(newComponents);
      state.setHasUnsavedChanges(true);
    },
    [state]
  );

  const handleSaveLayout = useCallback(async () => {
    if (!state.layoutName.trim()) {
      showToast('Please enter a layout name', 'error');
      return;
    }

    const hasContentArea = state.components.some(c => c.type === 'content-area');
    if (!hasContentArea) {
      showToast(
        'Layout must have a Content Area component to mark where page content will appear',
        'error'
      );
      return;
    }

    try {
      state.setIsSaving(true);

      if (isNewLayout) {
        const newLayout = await layoutsApi.create({
          siteId: siteId!,
          name: state.layoutName,
          description: state.layoutDescription || undefined,
          components: state.components,
          isDefault: state.isDefault,
        });

        showToast('Layout created successfully!', 'success', {
          showRoary: true,
          phraseKey: 'page_created',
        });
        navigate(`/sites/${siteId}/layouts/${newLayout.id}`, { replace: true });
      } else {
        const updatedLayout = await layoutsApi.update(layoutId!, {
          name: state.layoutName,
          description: state.layoutDescription || null,
          components: state.components,
          isDefault: state.isDefault,
        });

        // Keep layout state in sync so getPublishStatus() has fresh timestamps
        state.setLayout(updatedLayout);

        showToast('Layout saved successfully!', 'success', {
          showRoary: true,
          phraseKey: 'changes_saved',
        });
        state.setHasUnsavedChanges(false);
      }
    } catch (error: any) {
      console.error('Failed to save layout:', error);
      showToast(error?.message || 'Failed to save layout', 'error');
    } finally {
      state.setIsSaving(false);
    }
  }, [siteId, layoutId, isNewLayout, state, showToast, navigate]);

  const handleAddComponent = useCallback(
    (type: string, templateData?: any) => {
      if (isLayoutModeComponentType(type)) {
        const newComponent = {
          id: `${type}-${Date.now()}`,
          type,
          data: templateData || getDefaultDataForType(type, state.site, state.themeColors),
          order: 0,
          visible: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as Component;
        appendComponentToLayout(newComponent);
        return;
      }

      showToast(
        `Only ${LAYOUT_MODE_COMPONENTS.map(t => LAYOUT_COMPONENT_LABELS[t] || t).join(', ')} can be added to a layout.`,
        'warning'
      );
    },
    [state.site, state.themeColors, appendComponentToLayout, showToast]
  );

  const handleAddLibraryComponent = useCallback(
    async (libraryComponentId: string) => {
      try {
        setIsAddingComponent(true);
        const { component: source } = await libraryApi.getComponent(libraryComponentId);

        if (!source?.type || !isLayoutModeComponentType(source.type)) {
          showToast(
            `Only ${LAYOUT_MODE_COMPONENTS.map(t => LAYOUT_COMPONENT_LABELS[t] || t).join(', ')} can be added to a layout.`,
            'warning'
          );
          return;
        }

        const newComponent: Component = {
          ...source,
          id: `${source.type}-${Date.now()}`,
          order: 0,
          visible: source.visible !== false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        appendComponentToLayout(newComponent);
      } catch (error) {
        console.error('Failed to add library component to layout:', error);
        showToast('Failed to add component from library.', 'error');
      } finally {
        setIsAddingComponent(false);
      }
    },
    [appendComponentToLayout, showToast]
  );

  const handleDeleteComponent = useCallback(
    (component: Component) => {
      if (component.type === 'content-area') {
        showToast(
          'Cannot delete Content Area - every layout needs one to show page content',
          'warning'
        );
        return;
      }
      state.setDeleteModal({ isOpen: true, component });
    },
    [state, showToast]
  );

  const confirmDeleteComponent = useCallback(() => {
    const toDelete = state.deleteModal.component;
    if (toDelete) {
      state.setComponents(prev => prev.filter(c => c.id !== toDelete.id));
      state.setHasUnsavedChanges(true);
    }
    state.setDeleteModal({ isOpen: false, component: null });
  }, [state]);

  return {
    loadThemeColors,
    loadLayoutData,
    handleComponentsChange,
    handleSaveLayout,
    handleAddComponent,
    handleAddLibraryComponent,
    handleDeleteComponent,
    confirmDeleteComponent,
    isAddingComponent,
  };
};

function isLayoutModeComponentType(type: string): type is (typeof LAYOUT_MODE_COMPONENTS)[number] {
  return (LAYOUT_MODE_COMPONENTS as readonly string[]).includes(type);
}
